import { H2 } from '@/components'
import { Button } from '@/components_deprecated'
import { VEHICLE_SERVICE_PLANS_QUERY } from '@/modules/vehicles/queries'
import { useVehicleServicePlanHandlers } from '@/modules/vehicles/services/use-vehicle-service-plan-handlers'
import { VehicleServicePlansTable } from '@/modules/vehicles/services/vehicle-service-plans-table'
import type { EditVehicleServicePlanPromptResolve } from '@/modules/vehicles/types'
import type {
  VehicleServicePlansQuery,
  VehicleServicePlansQueryVariables,
} from '@/modules/vehicles/types.graphql'
import { getVehicleServicePlanInput } from '@/modules/vehicles/util'
import { useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import {
  useCreateWorkshopByName,
  useVehicleServicePlanMutations,
} from '../mutations.hooks'
import { EditVehicleServicePlanModal } from './edit-vehicle-service-plan-modal'

interface VehicleServicePlansProps {
  vehicleId: string
}

export const VehicleServicePlans: React.FC<VehicleServicePlansProps> = ({
  vehicleId,
}) => {
  const translations = useTranslate({
    vehicleServices: 'vehicles.vehicle-services',
    add: 'common.add',
  })

  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  const { data, loading } = useQuery<
    VehicleServicePlansQuery,
    VehicleServicePlansQueryVariables
  >(VEHICLE_SERVICE_PLANS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      vehicle: vehicleId,
      orderBy: 'scheduledStart',
    },
    onError: onErrorAuto(),
  })

  const servicePlans = useMemo(() => {
    return data?.allVehicleServicePlans?.edges?.map(edge => edge?.node) || []
  }, [data])

  const { createServicePlan } = useVehicleServicePlanMutations()
  const createWorkshopByName = useCreateWorkshopByName()
  const { editServicePlan, removeServicePlan, moveToExpirations } =
    useVehicleServicePlanHandlers()

  // Not the shared `addServicePlan`: that one asks which vehicle the service
  // belongs to, and here it is the vehicle whose page this is.
  async function handleCreate() {
    const { data } =
      await addPrompt<EditVehicleServicePlanPromptResolve | null>(resolve => (
        <EditVehicleServicePlanModal
          onCreateWorkshop={createWorkshopByName}
          onSubmit={resolve}
        />
      ))
    if (!data) return

    await createServicePlan({
      variables: {
        input: {
          ...getVehicleServicePlanInput(data.form, data.hasNotification),
          vehicle: vehicleId,
        },
      },
    })
  }

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-0">
        <H2>{translations.vehicleServices}</H2>
        {canAdd && (
          <Button
            iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
            height="3rem"
            onClick={handleCreate}
          >
            {translations.add}
          </Button>
        )}
      </div>

      <VehicleServicePlansTable
        servicePlans={servicePlans}
        loading={loading}
        showVehicle={false}
        onEdit={editServicePlan}
        onDelete={removeServicePlan}
        onMoveToExpirations={moveToExpirations}
      />
    </div>
  )
}
